import { FiLink } from 'react-icons/fi'
import { GoOrganization } from 'react-icons/go'
import { PiDatabase, PiUserCircle, PiUsers } from 'react-icons/pi'
import { RxLockClosed } from 'react-icons/rx'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { cn } from '~/@/lib/utils'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DataSourcesGuardLayout } from '~/components/data-sources/DataSourcesGuardLayout'
import { useCanViewDataSources } from '~/components/data-sources/useCanViewDataSources'
import { AdminGuardLayout } from '~/router/layouts'
import { lazy } from '~/router/lazy'
import { RouteObject } from '~/router/types'

interface SettingsOption {
  link: string
  name: string
  icon: JSX.Element
}

/**
 * OAuth callback routes for third-party integrations. Each provider redirects back to
 * /settings/connections/{name}?code=... after authorization. The matching callback component
 * at components/integration/callbacks/{name}.tsx validates params and creates the integration.
 * To add a new integration: add the provider slug here and create the callback component.
 */
const CONNECTION_PROVIDERS = [
  'gmail',
  'outlook',
  'hubspot',
  'netsuite',
  'ramp',
  'shipbob',
  'lazada',
  'shopee',
  'shopify',
  'tiktok-shop',
  'airtable',
  'faire',
  'amazon-seller',
  'quickbooks',
  'stripe',
  'xero',
  'beproduct',
  'sps-commerce',
  'loop-returns',
  'shopify/verify',
] as const

// Pre-resolve all callback modules at build time — supports any directory depth
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const callbackModules: Record<string, () => Promise<Record<string, any>>> = import.meta.glob(
  '../integration/callbacks/**/*.tsx',
)

const connectionCallbackRoutes: RouteObject[] = CONNECTION_PROVIDERS.map((provider) => ({
  path: `connections/${provider}`,
  lazy: () => {
    const modulePath = `../integration/callbacks/${provider}.tsx`
    const loader = callbackModules[modulePath]
    return loader().then((m) => ({ Component: m.default ?? Object.values(m)[0] }))
  },
}))

export const settingsRoute: RouteObject = {
  path: 'settings',
  Component: SettingsLayout,
  handle: {
    group: 'settings',
    crumb: 'breadcrumbs.settings',
    showLeftNav: false,
    pageType: 'settings',
  },
  children: [
    {
      index: true,
      lazy: lazy(() => import('./SettingsController')),
      handle: { crumb: 'breadcrumbs.profile' },
    },
    {
      path: 'members',
      lazy: lazy(() => import('./MembersController')),
      handle: { crumb: 'breadcrumbs.members' },
    },
    {
      path: 'connections',
      lazy: lazy(() => import('./ConnectionsController')),
      handle: { crumb: 'breadcrumbs.connections' },
    },
    ...connectionCallbackRoutes,
    {
      element: <DataSourcesGuardLayout />,
      children: [
        {
          path: 'data-sources',
          lazy: lazy(() => import('~/components/data-sources/DataSources')),
          handle: { crumb: 'breadcrumbs.data_sources' },
        },
      ],
    },
    {
      element: <AdminGuardLayout />,
      children: [
        {
          path: 'roles',
          lazy: lazy(() => import('./RolesController')),
          handle: { crumb: 'breadcrumbs.permissions' },
        },
        {
          path: 'roles/new',
          lazy: lazy(() => import('./CreateRoleController')),
          handle: { crumb: 'breadcrumbs.permissions' },
        },
        {
          path: 'roles/:role_id',
          lazy: lazy(() => import('./RoleController')),
          handle: { crumb: 'breadcrumbs.permissions' },
        },
      ],
    },
  ],
}

/** Settings sidebar nav on the left, matched child route (Outlet) on the right. */
function SettingsLayout() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { t } = useTypedTranslation()
  const { isAdmin, isInternal } = useGlobalContext()
  const { isMobile } = useDeviceType()
  const canViewDataSources = useCanViewDataSources()

  const usersTabDisplay = isAdmin || isInternal ? 'settings.manage_users' : 'settings.organization'

  const isSelected = (optionLink: string) => {
    // For the root settings page, only match exact path
    if (optionLink === '/settings') {
      return pathname === '/settings'
    }

    // Special handling for roles section
    if (optionLink === '/settings/roles') {
      return pathname === '/settings/roles'
    }

    // For other pages, match the specific route
    return pathname === optionLink
  }

  const SETTINGS_OPTIONS: SettingsOption[] = [
    {
      link: '/settings',
      name: t('settings.account.title'),
      icon: <PiUserCircle />,
    },
    {
      link: '/settings/members',
      name: t(usersTabDisplay),
      icon: isAdmin || isInternal ? <PiUsers /> : <GoOrganization />,
    },
    // Conditionally add the Roles option
    ...(isAdmin || isInternal
      ? [
          {
            link: '/settings/roles',
            name: t('settings.roles'),
            icon: <RxLockClosed />,
          },
        ]
      : []),
    // Only show integrations on desktop (non-mobile)
    ...(!isMobile
      ? [
          {
            link: '/settings/connections',
            name: t('settings.integrations'),
            icon: <FiLink />,
          },
        ]
      : []),
    ...(canViewDataSources && !isMobile
      ? [
          {
            link: '/settings/data-sources',
            name: t('settings.data_sources'),
            icon: <PiDatabase />,
          },
        ]
      : []),
  ]

  return (
    <div className="flex size-full flex-col md:flex-row">
      <div className="flex w-full flex-col border-b border-gray-4 p-4 md:w-[280px] md:min-w-[240px] md:border-b-0 md:border-e">
        <div>
          {SETTINGS_OPTIONS.map((option: SettingsOption) => (
            <div
              key={option.name}
              className={cn(
                'flex items-center gap-2 px-3 py-[10px] rounded cursor-pointer',
                isSelected(option.link) ? 'bg-gray-2' : 'hover:bg-gray-1',
              )}
              onClick={() => navigate(option.link)}
            >
              <div className={cn('text-lg', isSelected(option.link) ? 'text-blue-9' : '')}>
                {option.icon}
              </div>
              <div className="text-sm">{option.name}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  )
}
